// withSelectSkuId.js
'use strict';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import getProductData from '../selector/getProductData.js';
import getOperationData from '../selector/getOperationData.js';
import getModalData from '../selector/getModalData.js';
import getMeData from '../selector/getMeData.js';

import setDefaultShopCurrency from '../action/setDefaultShopCurrency.js';

import { SHOP_ID, ProductType } from '../resource/shopConstants.js';
import { addvalue as ShopModalId } from '../resource/modalId.js';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NULL_FUNCTION,
} from '../resource/defaults.js';

const withSelectSkuId = BaseComponent => {
  function CheckoutProduct(props) {
    const {
      shopId = null,
      productId = null,
      meId = null,
      productType = null,
      currency = null,
      isShopOpened = false,
      currencies = EMPTY_ARRAY,
      currencySkuId = EMPTY_OBJECT,
      children = NULL_FUNCTION,
      setUsingShopId = NULL_FUNCTION,
      setDefaultShopCurrency = NULL_FUNCTION,
      setSelectedSkuId = NULL_FUNCTION,
    } = props;
    const propsRef = useRef({});
    const prevPropsRef = useRef({
      meId,
      shopId,
      currency,
      productId,
      productType,
      currencySkuId,
      isShopOpened,
      currencies,
    });
    propsRef.current = {
      shopId,
      productId,
      meId,
      productType,
      currency,
      isShopOpened,
      currencies,
      currencySkuId,
      setUsingShopId,
      setDefaultShopCurrency,
      setSelectedSkuId,
    };

    const setUsingShopIdData = () => {
      const { productType, isShopOpened, setUsingShopId } = propsRef.current;
      // Should not update this value when shop is opened.
      // This is to prevent overwriting the value that the user has already selected.
      if (!productType || isShopOpened) {
        return;
      }
      return setUsingShopId({
        usingShopId:
          ProductType.MESSAGEPACKS === productType
            ? SHOP_ID.messagePack
            : SHOP_ID.addvalue,
      });
    };

    const setDefaultShopCurrencyData = () => {
      const { productId, isShopOpened, currencies, setDefaultShopCurrency } =
        propsRef.current;
      // Should not update this value when shop is opened.
      // This is to prevent overwriting the value that the user has already selected.
      if (!productId || !currencies.length || isShopOpened) {
        return;
      }
      return setDefaultShopCurrency({ productId });
    };

    const setSelectedSkuIdData = () => {
      const {
        currency,
        currencySkuId,
        shopId,
        isShopOpened,
        setSelectedSkuId,
      } = propsRef.current;
      const skuId = currencySkuId[currency];
      // Should not update this value when shop is opened.
      // This is to prevent overwriting the value that the user has already selected.
      if (!skuId || !shopId || isShopOpened) {
        return;
      }
      return setSelectedSkuId({
        shopId,
        skuId,
      });
    };

    useEffect(() => {
      setUsingShopIdData();
      setDefaultShopCurrencyData();
      setSelectedSkuIdData();
    }, []);

    useEffect(() => {
      const prevProps = prevPropsRef.current;
      const isProductTypeChanged = productType !== prevProps.productType;
      const isProductIdChanged = productId !== prevProps.productId;
      const isShopIdChanged = shopId !== prevProps.shopId;
      const isCurrencyChanged = currency !== prevProps.currency;
      const isCurrencySkuIdChanged = currencySkuId !== prevProps.currencySkuId;
      const isCurrenciesChanged = currencies !== prevProps.currencies;
      const isShopOpenChanged = isShopOpened !== prevProps.isShopOpened;
      const isMeIdChanged = meId !== prevProps.meId;
      if (isProductTypeChanged || isShopIdChanged || isShopOpenChanged) {
        setUsingShopIdData();
      }
      if (isProductIdChanged || isCurrenciesChanged || isShopOpenChanged) {
        setDefaultShopCurrencyData();
      }
      if (
        isShopIdChanged ||
        isCurrencyChanged ||
        isCurrencySkuIdChanged ||
        isMeIdChanged ||
        isShopOpenChanged
      ) {
        setSelectedSkuIdData();
      }
      prevPropsRef.current = {
        meId,
        shopId,
        currency,
        productId,
        productType,
        currencySkuId,
        isShopOpened,
        currencies,
      };
    }, [
      currencies,
      currency,
      currencySkuId,
      isShopOpened,
      meId,
      productId,
      productType,
      shopId,
    ]);

    return (
      <BaseComponent
        {...props}
        shopId={shopId}
        productId={productId}
        meId={meId}
        productType={productType}
        currency={currency}
        isShopOpened={isShopOpened}
        currencies={currencies}
        currencySkuId={currencySkuId}
        setUsingShopId={setUsingShopId}
        setDefaultShopCurrency={setDefaultShopCurrency}
        setSelectedSkuId={setSelectedSkuId}
      >
        {children}
      </BaseComponent>
    );
  }

  CheckoutProduct.propTypes = {
    shopId: PropTypes.string,
    productId: PropTypes.string,
    meId: PropTypes.string,
    productType: PropTypes.string,
    currency: PropTypes.string,
    isShopOpened: PropTypes.bool,
    currencies: PropTypes.array,
    currencySkuId: PropTypes.object,
    children: PropTypes.func,
    setUsingShopId: PropTypes.func,
    setDefaultShopCurrency: PropTypes.func,
    setSelectedSkuId: PropTypes.func,
  };

  const mapStateToProps = (state, { productId }) => {
    const shopId = getOperationData(state, ['shop'], 'usingShopId');
    return {
      shopId,
      productId,
      meId: getMeData(state, 'id'),
      productType: getProductData(state, [productId], 'type'),
      currency: getOperationData(state, ['shop'], 'currency'),
      isShopOpened:
        getModalData(state, ShopModalId.DIAMOND_PACK_SELECTOR, 'zIndex') !=
        null,
      currencies:
        getProductData(state, [productId], 'currencies') || EMPTY_ARRAY,
      currencySkuId:
        getProductData(state, [productId], 'currencySkuId') || EMPTY_OBJECT,
    };
  };

  const mapDispatchToProps = dispatch => {
    return {
      setUsingShopId: ({ usingShopId }) =>
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop'],
            data: {
              usingShopId,
            },
          },
        }),
      setSelectedSkuId: ({ skuId, shopId }) =>
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop', shopId],
            data: {
              selectedDiamondPackId: skuId,
            },
          },
        }),
      setDefaultShopCurrency: data => dispatch(setDefaultShopCurrency(data)),
    };
  };

  return connect(mapStateToProps, mapDispatchToProps)(CheckoutProduct);
};

export default withSelectSkuId;
